import {
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useResetTermsDisplay, useSaveTermsDisplay, useTermsDisplay, type TermsDisplayRule } from '@/api/hooks'
import { ErrorBanner } from '@/components/common'

/** The documents terms can be carried on, in the order a job passes through them. */
const DOCUMENTS = [
  { value: 'ALL', label: 'Every document' },
  { value: 'QUOTATION', label: 'Quotation' },
  { value: 'ORDER_CONFIRMATION', label: 'Order confirmation' },
  { value: 'PROFORMA', label: 'Proforma invoice' },
  { value: 'INVOICE', label: 'Tax invoice' },
  { value: 'RECEIPT', label: 'Receipt' },
  // The rate card handed across the counter. It commits nobody, but it quotes
  // prices, and what those prices assume belongs on it.
  { value: 'MENU_SHEET', label: 'Menu sheet' },
] as const

/**
 * Whether a document carries its terms as words, as a code to scan, or both.
 *
 * <p>Printed in full they are half a page nobody reads. As a code they are a
 * line and a square, and they stay current: the page behind the code is read
 * when somebody scans it, not when the document was printed.</p>
 *
 * <p>A row nobody has answered follows the nearest one that has been - this
 * service on every document, then every service on this document, then the
 * answer for everything - so an office sets one rule and varies it where it
 * has to, rather than filling in a grid of thirty.</p>
 */
export function TermsDisplayRules({
  serviceType,
  writable,
  hasClause,
}: {
  serviceType: string
  writable: boolean
  /** Whether any saved clause actually applies to this document. */
  hasClause: (document: string) => boolean
}) {
  const { data: rules, error } = useTermsDisplay()
  const save = useSaveTermsDisplay()
  const reset = useResetTermsDisplay()

  const written = (service: string, document: string) =>
    (rules ?? []).find((rule) => rule.serviceType === service && rule.documentType === document)

  /** What this row actually does, and whether it says so itself. */
  const effective = (document: string): { rule: TermsDisplayRule; own: boolean } => {
    const own = written(serviceType, document)
    if (own) return { rule: own, own: true }
    const nearest =
      written(serviceType, 'ALL')
      ?? written('ALL', document)
      ?? written('ALL', 'ALL')
      // Nothing written anywhere: the words, printed, which is what every
      // document did before there was a choice.
      ?? { serviceType, documentType: document, showText: true, showQr: false }
    return { rule: { ...nearest, serviceType, documentType: document }, own: false }
  }

  const set = (document: string, patch: Partial<TermsDisplayRule>) => {
    const { rule } = effective(document)
    save.mutate({ ...rule, serviceType, documentType: document, ...patch })
  }

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 3 }}>
        How these terms are carried
      </Typography>
      <Typography variant="caption" color="text.secondary">
        The code prints beside the clauses, or on its own where the words are switched off. It
        points at a page that reads the terms as they stand, so a document printed months ago never
        shows wording that has since changed.
      </Typography>

      <ErrorBanner error={error ?? save.error ?? reset.error} />

      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Document</TableCell>
            <TableCell align="center" width={120}>
              Printed words
            </TableCell>
            <TableCell align="center" width={120}>
              Code to scan
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {DOCUMENTS.map((document) => {
            const { rule, own } = effective(document.value)
            return (
              <TableRow key={document.value} hover>
                <TableCell>{document.label}</TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={rule.showText}
                    disabled={!writable || save.isPending}
                    onChange={(event) =>
                      set(document.value, { showText: event.target.checked })
                    }
                    inputProps={{ 'aria-label': `Print the words on the ${document.label}` }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={rule.showQr}
                    disabled={!writable || save.isPending}
                    onChange={(event) => set(document.value, { showQr: event.target.checked })}
                    inputProps={{ 'aria-label': `Print a code on the ${document.label}` }}
                  />
                </TableCell>
                <TableCell>
                  {/* Said rather than left to be guessed from two switches that
                      look identical whether they were set or inherited. */}
                  {own || document.value === 'ALL' ? null : (
                    <Tooltip title="Answered above; changing a switch here answers for this document alone">
                      <Typography variant="caption" color="text.secondary">
                        following
                      </Typography>
                    </Tooltip>
                  )}
                  {rule.showText || rule.showQr ? null : (
                    <Typography variant="caption" color="warning.main">
                      no terms on it
                    </Typography>
                  )}
                  {/* The switch alone prints nothing: the code points at
                      whatever clause is written for this document, and with
                      none written there is nothing for it to show. */}
                  {rule.showQr && document.value !== 'ALL' && !hasClause(document.value) && (
                    <Tooltip title="No clause under the Terms tab above is written for this document, so the code has nothing to point at">
                      <Typography variant="caption" color="warning.main" display="block">
                        no clause for this
                      </Typography>
                    </Tooltip>
                  )}
                  {/* An answer given here stays given, even after "Every
                      document" changes its mind - this is the only way back
                      to following it again. */}
                  {own && writable ? (
                    <Tooltip title="Stop answering for this document alone, and follow the rule above it again">
                      <span>
                        <IconButton
                          size="small"
                          disabled={reset.isPending}
                          onClick={() =>
                            reset.mutate({ serviceType, documentType: document.value })
                          }
                          aria-label={`Reset ${document.label} to follow the rule above it`}
                        >
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}
